import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps, DialogSize } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import { API_PATH_NOTIFICATION_LIST } from 'api/kinerja/useAPIKinerjaNotification'
import { assetUrl } from 'utils/url'
import { useApiPostResetPelaksanaanAction } from 'api/kinerja/kepala-sekolah/notification'
import { CardPelaksanaanStepResetKs } from 'app/KinerjaShared/components/CardPelaksanaanStepResetKs'
import { getStepsToResetInPreparationState } from 'app/KinerjaKSPage/utils/states'

import type {
  PelaksanaanAppealableStep,
  PelaksanaanState,
} from 'types/kinerja-ks-pelaksanaan'

const CONFIRMATION_TEXT_FORM_A =
  'Saya setuju bahwa nilai sebelumnya akan dihapus dan saya harus mengulang penilaian dan observasi dari awal.'
const CONFIRMATION_TEXT_DEFAULT =
  'Saya setuju bahwa nilai sebelumnya akan dihapus dan saya harus mengulang penilaian.'

type Props = Omit<DialogProps, 'children'> & {
  ksName: string
  requestId?: string // identifier for rollback request
  step: PelaksanaanAppealableStep
  requestorNpsn: string
  currentState: PelaksanaanState
  period?: { year?: number; no?: number }
}

export function DialogPelaksanaanAppealApprovalPs({
  isOpen,
  ksName,
  step,
  currentState,
  onClose,
  requestorNpsn,
  period,
  ...dialogProps
}: Props) {
  const formMethods = useForm()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeContent, setActiveContent] = useState<'FORM' | 'SUCCESS'>('FORM')

  const mutation = useApiPostResetPelaksanaanAction({ period })

  const handleSubmit = () => {
    mutation.mutate(
      { npsn: requestorNpsn, action: 'APPROVED' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
          queryClient.invalidateQueries(API_PATH_NOTIFICATION_LIST)
        },
        onError: () => {
          onClose()
          toast({ message: 'Gagal lanjut. Coba lagi.' })
        },
      }
    )
  }

  // Reset dialog upon dismissal
  useEffect(() => {
    if (!isOpen) {
      formMethods.reset()
      setActiveContent('FORM')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  let stepsToReset: Array<PelaksanaanState> = []
  switch (step) {
    case 'PREPARATION':
      stepsToReset = getStepsToResetInPreparationState(currentState)
      break
    case 'FOLLOW_UP':
      stepsToReset = ['FOLLOW_UP']

      if (
        currentState === 'REFLECTION_FOLLOW_UP_EVALUATION' ||
        currentState === 'COMPLETED'
      ) {
        stepsToReset.push('REFLECTION_FOLLOW_UP')
      }
      break
    case 'REFLECTION_FOLLOW_UP':
      stepsToReset = ['REFLECTION_FOLLOW_UP']
      break
  }

  const dialogSize: DialogSize =
    activeContent === 'SUCCESS' || step !== 'PREPARATION' ? 'sm' : 'md'
  const errorMessage = formMethods.formState.errors?.confirmationText?.message
  const isEmpty = !formMethods.formState.dirtyFields['confirmationText']
  const isLoadingSubmission = mutation.isLoading
  const confirmationText =
    step === 'PREPARATION'
      ? CONFIRMATION_TEXT_FORM_A
      : CONFIRMATION_TEXT_DEFAULT

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size={dialogSize}
      onClose={onClose}
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeContent === 'FORM' && (
          <form
            onSubmit={formMethods.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <Text variant="display-sm">
              {!ksName
                ? 'Nilai ulang dokumen?'
                : step === 'PREPARATION'
                ? `Nilai ulang seluruh dokumen milik ${ksName} dan observasi dari awal?`
                : `Nilai ulang dokumen milik ${ksName}?`}
            </Text>
            <div
              className={clsx(
                'grid gap-4',
                dialogSize === 'sm' ? 'grid-cols-1' : 'sm:grid-cols-2'
              )}
            >
              {stepsToReset.map((step, i) => (
                <CardPelaksanaanStepResetKs
                  step={step}
                  className="max-w-sm"
                  key={i}
                />
              ))}
            </div>
            <Text variant="body-lg" color="subdued">
              {step === 'PREPARATION'
                ? 'Kepala Sekolah dapat mengubah isi dokumen dan Anda harus mengulang observasi serta penilaian seluruh dokumen dari awal.'
                : 'Kepala Sekolah dapat mengubah isi dokumen dan Anda harus mengulang penilaian.'}
            </Text>
            <Text variant="body-lg" color="subdued">
              Mohon ketik kalimat ini pada kolom kalimat persetujuan:
            </Text>

            <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
              <Text variant="heading-sm" color="inherit">
                {confirmationText}
              </Text>
            </div>

            <div
              className={clsx(
                'space-y-1',
                dialogSize === 'sm' ? 'max-w-xs' : 'max-w-lg'
              )}
            >
              <Text as="label" htmlFor="confirmation-text">
                Kalimat persetujuan
              </Text>
              <Textarea
                isInvalid={!!errorMessage}
                errorMessage={errorMessage}
                id="confirmation-text"
                placeholder="Ketik di sini."
                rows={3}
                {...formMethods.register('confirmationText', {
                  validate: (value) =>
                    value === confirmationText ||
                    'Mohon cek ejaan dan coba lagi.',
                })}
              />
            </div>

            <div className="flex space-x-3 border-t pt-4 justify-end">
              <Button
                color="white"
                disabled={isLoadingSubmission}
                size="lg"
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                size="lg"
                disabled={isLoadingSubmission || isEmpty}
                isLoading={isLoadingSubmission}
                type="submit"
              >
                Lanjut
              </Button>
            </div>
          </form>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl(
                '/illustrations/img-reflection-documents-magnifying-glass.png'
              )}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text variant="display-sm" className="mb-4">
              Pengajuan perubahan diterima
            </Text>
            <Text variant="body-lg" color="subdued" className="mb-6">
              Setelah Kepala Sekolah selesai mengubah isi dokumen, Anda dapat
              mengulang penilaian Anda.
            </Text>
            <Button size="lg" onClick={() => onClose?.()}>
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
